import { useMemo, useState } from "react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { differenceInBusinessDays } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CustomReducedDto } from "@/types/api/api-types";
import { PackagingTypeBadge } from "@/components/shared/badge/packagingType.badge";
import { Incoterm } from "@/components/shared/incoterm";
import { ActionDataBo } from "@/types/tableAction";
import { User, userAtom } from "@/stores/authStore";
import { cn } from "@/lib/utils";
import {
  CircleX,
  MailCheck,
  MessageCircle,
  Paperclip,
  PackageCheck,
  Pencil,
  Plane,
  Plus,
  Send,
  Truck,
  UserRound,
} from "lucide-react";
import { useAtomValue } from "jotai";
import {
  useGetOneExpeditionBo,
  useMarkDhlTntShipped,
  useUpdateExpeditionBo,
} from "@/api/expedition.api";
import { ConfirmationModal } from "@/components/shared/confirmation.modal";
import ExpressFullIcon from "@/assets/icons/express-full.svg?react";
import { ExpeditionPickupSelectedByEnum } from "@/constants/enums/expeditionPickupSelectedBy.enum";
import { DeliveryStatusBadge } from "@/components/shared/badge/deliveryStatus.badge";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { Button } from "@/components/ui/button";
import Paths from "@/constants/paths";
import { BlockedReasonSelect } from "@/components/backOffice/validatedExpeditions/blockedReasonSelect";
import { Copy, File, Table } from "lucide-react";
import { toast } from "react-toastify";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import {
  BO_SIDE_PANEL_EXPEDITION,
  FileAction,
  getVisibleFileTypes,
  sortDocumentsByPriority,
} from "@/constants/fileCrud";
import { DocumentsUpdateModal } from "@/components/shared/modal/documentsUpdate.modal";
import { handleOpenDocument } from "@/lib/file.util";
import { NoteType } from "@/constants/enums/noteType.enum";
import {
  LateShippingQualificationBadge,
  getLateShippingQualificationNote,
} from "@/components/backOffice/shared/lateShippingQualificationBadge";
import {
  FORTY_EIGHT_HOURS_MS,
  Row,
  getExpeditionRef,
  getInitials,
  isDhlTntTracking,
} from "./expeditionColumnsUtils";

export {
  expeditionRowClassname,
  handleExpeditionRowClick,
  resolveExpeditionRowTarget,
} from "./expeditionColumnsUtils";

const columnHelper = createColumnHelper<Row>();

type ExpeditionsT = TFunction<"boExpeditions">;

const Translated = ({ render }: { render: (t: ExpeditionsT) => string }) => {
  const { t } = useTranslation("boExpeditions");
  return <>{render(t)}</>;
};

const translatedHeader = (render: (t: ExpeditionsT) => string) => () => (
  <Translated render={render} />
);

const DELIVERY_BADGE_STYLES = {
  client: { bg: "rgba(58, 242, 75, 0.2)", pill: "#00810c" },
  pelican: { bg: "rgba(1, 121, 111, 0.1)", pill: "#01796f" },
} as const;

export function DeliveryBadge({ byClient, date }: { byClient: boolean; date: string }) {
  const { t } = useTranslation("boExpeditions");
  const styles = byClient ? DELIVERY_BADGE_STYLES.client : DELIVERY_BADGE_STYLES.pelican;
  return (
    <div className="flex w-full items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex rounded-full px-2.5 py-0.5" style={{ backgroundColor: styles.bg }}>
            <span
              className="text-[.75rem] leading-tight font-semibold uppercase"
              style={{ color: styles.pill }}
            >
              {t("deliveryBadge.label", { date })}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {byClient ? t("deliveryBadge.requestedByClient") : t("deliveryBadge.requestedByPelican")}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export const selectColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "select",
    header: function SelectAllHeader({ table }) {
      const { t } = useTranslation("boExpeditions");
      return (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() ? "indeterminate" : false)
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("selectColumn.selectAll")}
          className="mx-1 mr-2 ml-0"
        />
      );
    },
    cell: function SelectRowCell({ row }) {
      const { t } = useTranslation("boExpeditions");
      return (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("selectColumn.selectRow")}
          className="mx-1 mr-2"
        />
      );
    },
    size: 44,
    enableSorting: false,
  });

// `columnHelper.accessor` infère un TValue concret (ex. `string | undefined`) non assignable
// à `ColumnDef<Row>` (TValue par défaut `unknown`) à cause d'une incompatibilité de variance
// sur `footer`/`cell` — on reste donc en littéral objet + `accessorKey` pour ces colonnes.
export const finalizedAtColumn = (
  setActionData?: (actionData: ActionDataBo) => void,
): ColumnDef<Row> => ({
  id: "finalizedAt",
  accessorKey: "finalizedAt",
  header: translatedHeader((t) => t("columns.finalizedAt")),
  sortDescFirst: true,
  size: 180,
  cell: function FinalizedAtCell(info) {
    const { t } = useTranslation("boExpeditions");
    const row = info.row.original;
    if (!row.finalizedAt) return null;

    const finalizedAt = new Date(row.finalizedAt);
    const finalizedBy = row.finalizedBy;
    const initials = finalizedBy ? getInitials(finalizedBy) : null;

    const isShippingLate = !!row.isShippingLate;
    const isQualified = !!getLateShippingQualificationNote(row.expeditionNotes);
    const businessDaysSinceFinalized = differenceInBusinessDays(new Date(), finalizedAt);

    return (
      <div className="flex flex-col gap-0">
        <div className="flex items-center gap-1.5">
          <span className={cn("text-[1rem] text-black", isShippingLate && "text-red-600")}>
            {finalizedAt.toLocaleDateString("fr-FR")}
          </span>
          {isShippingLate && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex shrink-0 rounded-full bg-red-600/10 px-2 py-0.5">
                  <span className="text-[.75rem] leading-tight font-semibold text-red-600">
                    {t("finalizedAtCell.notShippedBadge", { days: businessDaysSinceFinalized })}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {t("finalizedAtCell.notShippedTooltip", { count: businessDaysSinceFinalized })}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <span className={cn("text-xs text-black", isShippingLate && "text-red-600")}>
          {finalizedAt.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
          {initials && ` - ${initials}`}
        </span>
        {isShippingLate && !isQualified && setActionData && (
          <Button
            type="button"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setActionData({ expedition: row, action: "LATE_SHIPPING_QUALIFICATION" });
            }}
            className="mt-1 h-fit w-fit border-1 border-red-600 bg-white !py-0.5 text-xs text-red-600 shadow-none hover:bg-red-50 hover:text-red-700"
          >
            {t("finalizedAtCell.qualify")}
          </Button>
        )}
      </div>
    );
  },
});

export const pickupDateColumn = (options?: {
  header?: string;
  showDeliveryBadge?: boolean;
  showLate?: boolean;
}): ColumnDef<Row> => ({
  id: "pickupDate",
  accessorKey: "pickupDate",
  header: options?.header ?? translatedHeader((t) => t("columns.pickupDate")),
  sortDescFirst: false,
  size: options?.showDeliveryBadge ? 140 : 110,
  cell: function PickupDateCell(info) {
    const { t } = useTranslation("boExpeditions");
    const row = info.row.original;
    if (!row.pickupDate) return null;

    const skipPickup = !!row.pickupInfo?.skipPickup;
    const pickupByClient =
      !skipPickup && row.pickupInfo?.pickupSelectedBy === ExpeditionPickupSelectedByEnum.CLIENT;
    const pickupByDriver =
      !skipPickup && row.pickupInfo?.pickupSelectedBy === ExpeditionPickupSelectedByEnum.DRIVER;

    const pickupIcons = (
      <>
        {skipPickup && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleX className="h-5 w-5 text-gray-600" />
            </TooltipTrigger>
            <TooltipContent>{t("pickupDateCell.noPickupNeeded")}</TooltipContent>
          </Tooltip>
        )}
        {pickupByClient && (
          <Tooltip>
            <TooltipTrigger asChild>
              <UserRound className="h-5 w-5 text-gray-600" />
            </TooltipTrigger>
            <TooltipContent>{t("pickupDateCell.selectedByClient")}</TooltipContent>
          </Tooltip>
        )}
        {pickupByDriver && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Truck className="h-5 w-5 text-gray-600" />
            </TooltipTrigger>
            <TooltipContent>{t("pickupDateCell.selectedByPelican")}</TooltipContent>
          </Tooltip>
        )}
      </>
    );

    if (!options?.showDeliveryBadge) {
      return (
        <div className="flex items-center gap-1.5">
          <span className="text-md font-semibold text-black">
            {new Date(row.pickupDate).toLocaleDateString("fr-FR")}
          </span>
          {pickupIcons}
        </div>
      );
    }

    const deliveryFromDate = row.deliveryInfo.deliveryFromDate;

    return (
      <div className="flex w-fit flex-col gap-1 pl-1">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              "text-[1rem] font-semibold text-gray-900",
              options?.showLate && row.isLate && "text-red-600",
            )}
          >
            {new Date(row.pickupDate).toLocaleDateString("fr-FR")}
          </span>
          {pickupIcons}
        </div>
        {deliveryFromDate && (
          <DeliveryBadge
            byClient={!!row.deliveryInfo.deliveryFromSelectedByClient}
            date={new Date(deliveryFromDate).toLocaleDateString("fr-FR")}
          />
        )}
      </div>
    );
  },
});

export const envoiColumn = (options?: { header?: string }): ColumnDef<Row> =>
  columnHelper.display({
    id: "envoi",
    header: options?.header ?? translatedHeader((t) => t("columns.envoi")),
    size: 40,
    enableSorting: false,
    meta: { cellClassName: "p-0" },
    cell: function EnvoiCell(info) {
      const { t } = useTranslation("boExpeditions");
      const row = info.row.original;
      const isExpress = row.expeditionOptions.express;
      const isRemote =
        !row.pickupInfo?.pickupLocation?.pickupZones ||
        row.pickupInfo?.pickupLocation?.pickupZones?.remote;

      return (
        <div className="flex w-full shrink-0 flex-col items-center justify-center gap-1">
          {isExpress && (
            <Tooltip>
              <TooltipTrigger>
                <ExpressFullIcon className="h-6 w-6" />
              </TooltipTrigger>
              <TooltipContent>{t("envoiCell.express")}</TooltipContent>
            </Tooltip>
          )}
          {isRemote && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Plane className="h-5 w-5 text-black" fill="currentColor" stroke="none" />
              </TooltipTrigger>
              <TooltipContent>{t("envoiCell.remotePickup")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  });

const DocsModalContent = ({
  expeditionId,
  onClose,
}: {
  expeditionId: string;
  onClose: () => void;
}) => {
  const { data: expedition, isLoading } = useGetOneExpeditionBo(expeditionId);

  const visibleFileTypes = [
    FileTypeEnum.EXPEDITION_INVOICE,
    FileTypeEnum.EXPEDITION_FORM,
    FileTypeEnum.EXPEDITION_FORM_EXCEL,
    FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE,
    FileTypeEnum.EXPEDITION_OTHER,
  ];

  const documents = sortDocumentsByPriority(
    (expedition?.expeditionDocuments?.documents ?? []).filter(
      (document) =>
        getVisibleFileTypes(FileAction.BO_SIDE_PANEL_EXPEDITION).includes(
          document.type as FileTypeEnum,
        ) && visibleFileTypes.includes(document.type as FileTypeEnum),
    ),
  );

  return (
    <DocumentsUpdateModal
      open
      onClose={onClose}
      expeditionId={expeditionId}
      documents={documents}
      expeditionDocumentsId={
        expedition?.expeditionDocuments && "id" in expedition.expeditionDocuments
          ? expedition.expeditionDocuments.id
          : undefined
      }
      invalidateQueryKey={["expedition", "all", "bo"]}
      isBo
      expedition={expedition}
      fileContextRules={BO_SIDE_PANEL_EXPEDITION}
      isLoading={isLoading}
    />
  );
};

function DocsCell({ row }: { row: Row }) {
  const [open, setOpen] = useState(false);

  const isCustomQuotation = row.quotation === null;
  if (isCustomQuotation) return null;

  return (
    <>
      <button
        type="button"
        className="mx-auto flex cursor-pointer items-center justify-center rounded p-1 text-gray-600 hover:bg-gray-100"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Paperclip className="h-5 w-5" />
      </button>
      {open && <DocsModalContent expeditionId={row.id} onClose={() => setOpen(false)} />}
    </>
  );
}

export const docsColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "docs",
    header: translatedHeader((t) => t("columns.docs")),
    size: 60,
    enableSorting: false,
    cell: (info) => (
      <div className="flex justify-center">
        <DocsCell row={info.row.original} />
      </div>
    ),
  });

const ExpeditionIdentifierRef = ({ identifier, color }: { identifier: string; color: string }) => (
  <div className="flex flex-row items-center gap-1.5">
    <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
    <span
      className="cursor-default text-[1rem] font-medium text-black"
      onClick={(e) => e.stopPropagation()}
    >
      {identifier}
    </span>
  </div>
);

const ExpeditionNotesPreview = ({
  row,
  setActionData,
  ignoredNoteTypes,
  showAddNotePlaceholder,
}: {
  row: Row;
  setActionData: (actionData: ActionDataBo) => void;
  ignoredNoteTypes?: NoteType[];
  showAddNotePlaceholder?: boolean;
}) => {
  const { t } = useTranslation("boExpeditions");
  const previewNotes =
    row.expeditionNotes?.filter(
      (note) =>
        note.type !== NoteType.LATE_SHIPPING_QUALIFICATION &&
        !ignoredNoteTypes?.includes(note.type as NoteType),
    ) ?? [];
  const latest = previewNotes[0];

  if (!latest) {
    if (!showAddNotePlaceholder) return null;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="mt-0.5 flex w-fit cursor-pointer items-center justify-center gap-0.5 rounded-md bg-blue-100 p-1 text-blue-600"
            onClick={(e) => {
              e.stopPropagation();
              setActionData({ expedition: row, action: "NOTES" });
            }}
          >
            <Pencil className="size-3.5 shrink-0" />
            <Plus className="size-3 shrink-0" />
          </div>
        </TooltipTrigger>
        <TooltipContent>{t("notesPreview.addNote")}</TooltipContent>
      </Tooltip>
    );
  }

  const initials = getInitials(latest.createdBy);

  const additionalNotesCount = previewNotes.length - 1;

  return (
    <div
      className="mt-0.5 flex w-full cursor-pointer items-center gap-1 rounded-md bg-blue-100 p-1 text-blue-600"
      onClick={(e) => {
        e.stopPropagation();
        setActionData({ expedition: row, action: "NOTES" });
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex w-full flex-1 items-center gap-1 overflow-hidden">
            <span className="shrink-0 text-[10px] font-semibold">{initials}</span>
            <p className="truncate text-[.85rem]">{latest.note}</p>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[280px] p-2" side="bottom">
          {latest.note}
        </TooltipContent>
      </Tooltip>
      {additionalNotesCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0 text-[.9rem] leading-none font-bold text-blue-800">
              +{additionalNotesCount}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px] p-2" side="bottom">
            {t("notesPreview.showOtherNotes")}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export const identifierColumn = (
  setActionData: (actionData: ActionDataBo) => void,
  options?: {
    canResetDraft?: boolean;
    header?: string;
    cotationOnly?: boolean;
    hideDraft?: boolean;
  },
): ColumnDef<Row> =>
  columnHelper.display({
    id: "identifier",
    header: options?.header ?? translatedHeader((t) => t("columns.identifier")),
    size: options?.cotationOnly ? 100 : 228,
    meta: { cellClassName: "p-2 pr-0" },
    cell: function IdentifierCell(info) {
      const { t } = useTranslation("boExpeditions");
      const row = info.row.original;
      const isDraft = !!row.draftBy;
      const draftBy = row.draftBy;

      const ref = getExpeditionRef(row);

      const draftInitials = draftBy ? getInitials(draftBy) : null;
      const draftDate = row.updatedAt ? new Date(row.updatedAt).toLocaleDateString("fr-FR") : null;
      const draftTime = row.updatedAt
        ? new Date(row.updatedAt).toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : null;

      const pickupLocationColor = row.pickupInfo?.pickupLocationColor || "#a4a4a4";

      const draftEditedOver48h = isDraft
        ? row.updatedAt
          ? Date.now() - new Date(row.updatedAt).getTime() > FORTY_EIGHT_HOURS_MS
          : false
        : false;
      const hasRecentNote =
        row.expeditionNotes?.some(
          (note) => Date.now() - new Date(note.createdAt).getTime() <= FORTY_EIGHT_HOURS_MS,
        ) ?? false;
      const showDraftOverdue = draftEditedOver48h && !hasRecentNote;

      return (
        <div className="relative flex max-w-[220px] flex-row items-center gap-2">
          <div className="flex w-full min-w-0 flex-col gap-1">
            <div className="flex items-center justify-between gap-1.5">
              <ExpeditionIdentifierRef identifier={ref} color={pickupLocationColor} />
              {isDraft && !options?.cotationOnly && !options?.hideDraft && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {options?.canResetDraft ? (
                      <button
                        className={cn(
                          "flex cursor-pointer items-center gap-1 rounded-full px-1.5 py-1 hover:bg-gray-200",
                          showDraftOverdue && "bg-red-100",
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionData({ expedition: row, action: "RESET_DRAFT_OWNER" });
                        }}
                      >
                        <Pencil
                          className={cn(
                            "h-4 w-4",
                            showDraftOverdue ? "text-red-600" : "text-gray-600",
                          )}
                        />
                        {showDraftOverdue && (
                          <span className="text-xs font-semibold text-red-600">+48h</span>
                        )}
                      </button>
                    ) : (
                      <span className="flex items-center gap-1 rounded-full bg-gray-100 px-1.5 py-1">
                        <Pencil
                          className={cn(
                            "h-3 w-3",
                            showDraftOverdue ? "text-red-600" : "text-gray-600",
                          )}
                        />
                        {showDraftOverdue && (
                          <span className="text-xs font-semibold text-red-600">+48h</span>
                        )}
                      </span>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {t("identifierCell.draftTitle")}
                      <br />
                      {draftDate && draftTime
                        ? t("identifierCell.draftLastEditedByAt", {
                            initials: draftInitials,
                            date: draftDate,
                            time: draftTime,
                          })
                        : t("identifierCell.draftLastEditedBy", { initials: draftInitials })}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {!options?.cotationOnly && (
              <>
                <ExpeditionNotesPreview row={row} setActionData={setActionData} />
                <LateShippingQualificationBadge row={row} setActionData={setActionData} />
              </>
            )}
          </div>
        </div>
      );
    },
  });

const PackagingDimensionsBadge = ({
  row,
  setActionData,
}: {
  row: Row;
  setActionData: (actionData: ActionDataBo) => void;
}) => {
  const { t } = useTranslation("boExpeditions");
  const dimensionsNote = row.expeditionNotes?.find(
    (note) => note.type === NoteType.PACKAGING_DIMENSIONS,
  );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActionData({ expedition: row, action: "PACKAGING_DIMENSIONS" });
  };

  if (dimensionsNote) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="shrink-0 rounded-md border border-orange-300 bg-orange-100 px-2 py-1 text-[11px] font-semibold text-orange-700 hover:bg-orange-200"
      >
        {dimensionsNote.note}
      </button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      onClick={handleClick}
      className="h-fit border-1 border-orange-500 bg-white !py-1 text-orange-500 shadow-none hover:bg-orange-100 hover:text-orange-700"
    >
      {t("packagingDimensionsBadge.palette")}
    </Button>
  );
};

export const packagingIdentifierColumn = (
  setActionData: (actionData: ActionDataBo) => void,
  options?: { header?: string; hideDimensions?: boolean },
): ColumnDef<Row> =>
  columnHelper.display({
    id: "identifier",
    header: options?.header ?? translatedHeader((t) => t("columns.identifier")),
    size: options?.hideDimensions ? 220 : 260,
    meta: { cellClassName: "p-2 pr-0" },
    cell: (info) => {
      const row = info.row.original;

      const ref = getExpeditionRef(row);
      const pickupLocationColor = row.pickupInfo?.pickupLocationColor || "#a4a4a4";

      return (
        <div className="relative flex max-w-[250px] flex-col justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-row justify-between gap-1">
            <ExpeditionIdentifierRef identifier={ref} color={pickupLocationColor} />
            {!options?.hideDimensions && (
              <PackagingDimensionsBadge row={row} setActionData={setActionData} />
            )}
          </div>
          <ExpeditionNotesPreview
            row={row}
            setActionData={setActionData}
            ignoredNoteTypes={[NoteType.PACKAGING_DIMENSIONS]}
            showAddNotePlaceholder
          />
          <LateShippingQualificationBadge row={row} setActionData={setActionData} />
        </div>
      );
    },
  });

export const comColumn = (
  _setActionData: (actionData: ActionDataBo) => void,
  options?: { minified?: boolean },
): ColumnDef<Row> =>
  columnHelper.display({
    id: "com",
    header: translatedHeader((t) => t("columns.com")),
    size: options?.minified ? 50 : 300,
    enableSorting: false,
    cell: (info) => {
      const comment = info.row.original.deliveryInfo?.comment;
      if (!comment) return null;

      if (options?.minified) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex cursor-pointer items-center justify-center rounded-full bg-gray-200 py-2">
                <MessageCircle className="h-5 w-5 text-gray-500" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[300px]">
              <p className="text-xs">{comment}</p>
            </TooltipContent>
          </Tooltip>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="max-w-[250px] cursor-default rounded-md bg-gray-100 p-2 xl:max-w-[300px]">
              <p className="line-clamp-2 min-w-[120px] text-[.875rem] whitespace-normal text-gray-700">
                <MessageCircle className="mr-1 inline-block h-4 w-4 shrink-0 align-text-top text-gray-500" />
                {comment}
              </p>
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]">
            <p className="text-xs">{comment}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
  });

export const orderColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "order",
    header: translatedHeader((t) => t("columns.order")),
    size: 60,
    enableSorting: false,
    cell: (info) => {
      const documents = info.row.original.expeditionDocuments?.documents;
      const order =
        documents?.find((d) => d.type === FileTypeEnum.EXPEDITION_QUOTE) ||
        documents?.find((d) => d.type === FileTypeEnum.EXPEDITION_QUOTE_EXCEL);
      if (!order) return null;

      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDocument(order.path);
          }}
          className="flex w-full items-center justify-center text-gray-700 hover:text-black"
        >
          {order.type === FileTypeEnum.EXPEDITION_QUOTE_EXCEL ? (
            <File className="h-5 w-5" />
          ) : (
            <Table className="h-5 w-5" />
          )}
        </button>
      );
    },
  });

const MARK_DHL_TNT_SHIPPED_ROLES = ["PACKER", "ADMIN", "SUPERADMIN", "MEMBER"];

const DhlTntShippedBadge = ({ firstScanAt }: { firstScanAt: string }) => {
  const { t } = useTranslation("boExpeditions");
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex shrink-0 items-center rounded p-1 text-teal-600">
          <PackageCheck className="h-5 w-5" />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {t("dhlTntShipped.shippedTooltip", {
          date: new Date(firstScanAt).toLocaleDateString("fr-FR"),
        })}
      </TooltipContent>
    </Tooltip>
  );
};

function MarkDhlTntShippedButton({ row }: { row: Row }) {
  const { t } = useTranslation("boExpeditions");
  const user = useAtomValue(userAtom);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { mutate: markDhlTntShipped, isPending } = useMarkDhlTntShipped();

  const firstScanAt = row.tracking?.firstScanAt;
  if (firstScanAt) return <DhlTntShippedBadge firstScanAt={firstScanAt} />;

  if (!user || !MARK_DHL_TNT_SHIPPED_ROLES.includes(user.role)) return null;

  return (
    <>
      {isConfirmOpen && (
        <ConfirmationModal
          open={true}
          onClose={() => setIsConfirmOpen(false)}
          title={t("dhlTntShipped.confirmTitle")}
          text={t("dhlTntShipped.confirmDescription")}
          confirm={() => {
            markDhlTntShipped(row.id);
            setIsConfirmOpen(false);
          }}
        />
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled={isPending}
            className="shrink-0 cursor-pointer rounded p-1 text-amber-600 hover:bg-amber-100 disabled:opacity-50"
            onClick={(e) => {
              e.stopPropagation();
              setIsConfirmOpen(true);
            }}
          >
            <PackageCheck className="h-5 w-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("dhlTntShipped.markTooltip")}</TooltipContent>
      </Tooltip>
    </>
  );
}

// `dhlTntShipped` pilote ce qui est rendu à droite du lien de suivi d'une expédition DHL/TNT :
// - "action" : le bouton de marquage manuel, remplacé par l'état « expédiée » une fois marquée
// - "status" : uniquement l'état « expédiée », en lecture seule
// - absent : rien (comportement des tables qui n'ont pas à gérer ce marquage)
const DhlTntShippedCell = ({ row, mode }: { row: Row; mode: "action" | "status" | undefined }) => {
  if (!mode || !isDhlTntTracking(row.tracking)) return null;
  if (mode === "action") return <MarkDhlTntShippedButton row={row} />;

  const firstScanAt = row.tracking?.firstScanAt;
  if (!firstScanAt) return null;
  return <DhlTntShippedBadge firstScanAt={firstScanAt} />;
};

export const trackingColumn = (options?: { dhlTntShipped?: "action" | "status" }): ColumnDef<Row> =>
  columnHelper.display({
    id: "tracking",
    header: translatedHeader((t) => t("columns.tracking")),
    size: options?.dhlTntShipped ? 170 : 150,
    enableSorting: false,
    cell: function TrackingCell(info) {
      const { t } = useTranslation("boExpeditions");
      const tracking = info.row.original.tracking;

      if (tracking?.manualTracking === false && tracking?.trackingCode) {
        return (
          <div className="group relative flex max-w-[140px] items-center">
            <Button
              variant="link"
              className="max-w-[140px] truncate overflow-hidden px-1 text-sm font-medium"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`${Paths.others.tracking}?code=${info.row.original.id}`, "_blank");
              }}
            >
              {tracking.trackingCode}
            </Button>
            <button
              type="button"
              className="absolute top-1/2 right-0 -translate-y-1/2 rounded-full bg-white p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 focus:outline-hidden"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(tracking.trackingCode!);
                toast.success(t("trackingCell.copySuccess"));
              }}
            >
              <Copy width={16} height={16} className="text-gray-500" />
            </button>
          </div>
        );
      } else if (tracking?.manualTracking === true && tracking?.trackingUrl) {
        return (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="link"
              className="max-w-[140px] truncate overflow-hidden p-0 text-sm font-medium"
              onClick={(e) => {
                e.stopPropagation();
                window.open(tracking.trackingUrl, "_blank");
              }}
            >
              {t("trackingCell.trackingLink")}
            </Button>
            <DhlTntShippedCell row={info.row.original} mode={options?.dhlTntShipped} />
          </div>
        );
      }
      return <span className="px-1 text-sm text-gray-400">-</span>;
    },
  });

export const clientColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "client",
    header: translatedHeader((t) => t("columns.client")),
    size: 282,
    meta: { cellClassName: "bg-gray-100/60" },
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex flex-col gap-1 px-1 py-2">
          <span
            className="w-fit max-w-[250px] cursor-default truncate text-[1rem] font-semibold text-gray-900"
            title={row.company.businessName}
            onClick={(e) => e.stopPropagation()}
          >
            {row.company.businessName}
          </span>
          <span
            className="w-fit max-w-[250px] cursor-default truncate text-xs text-black"
            title={row.internalName ?? undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {row.internalName ?? " "}
          </span>
        </div>
      );
    },
  });

export const clientDestColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "clientDest",
    header: translatedHeader((t) => t("columns.clientDest")),
    size: 212,
    meta: { cellClassName: "bg-gray-100/60" },
    cell: (info) => {
      const row = info.row.original;
      const company = row.deliveryInfo.company?.trim() !== "" ? row.deliveryInfo.company : null;
      const fullName =
        row.deliveryInfo.firstName && row.deliveryInfo.lastName
          ? `${row.deliveryInfo.firstName} ${row.deliveryInfo.lastName}`
          : null;

      return (
        <div className="flex flex-col gap-1 py-2">
          <span
            className="w-fit max-w-[180px] cursor-default truncate text-[1rem] font-semibold text-gray-900"
            title={company || fullName || undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {company || fullName || "-"}
          </span>
          {company && fullName && (
            <span
              className="w-fit max-w-[180px] cursor-default truncate text-xs text-black"
              title={fullName ?? undefined}
              onClick={(e) => e.stopPropagation()}
            >
              {fullName ?? " "}
            </span>
          )}
        </div>
      );
    },
  });

// based of règle douanière (= custom) and not delivery country
export const countryColumn = (customs: CustomReducedDto[]): ColumnDef<Row> =>
  columnHelper.display({
    id: "country",
    header: translatedHeader((t) => t("columns.country")),
    size: 180,
    meta: { cellClassName: "bg-gray-100/60" },
    cell: (info) => {
      const row = info.row.original;
      const customId = row.quotation?.customId ?? row.customQuotation?.customId;
      const custom = customs.find((c) => c.id === customId);
      return (
        <div className="flex max-w-[150px] items-center gap-2 truncate px-2">
          {custom?.flagCode && (
            <img
              src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${custom.flagCode.toUpperCase()}.svg`}
              className="h-4 w-4 shrink-0"
              alt=""
            />
          )}
          <span className="truncate text-gray-900">{custom?.label ?? "-"}</span>
        </div>
      );
    },
  });

export const btlsVolColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "btlsVol",
    header: translatedHeader((t) => t("columns.btlsVol")),
    size: 100,
    cell: function BtlsVolCell(info) {
      const { t } = useTranslation("boExpeditions");
      const row = info.row.original;
      const quotation = row.quotation;
      if (!quotation) return <span className="px-2 text-sm text-gray-400">-</span>;

      const nbBottles =
        quotation.deliveryContent?.reduce((acc, c) => acc + c.numberOfBottles, 0) ||
        quotation.quotationProducts?.reduce((acc, p) => acc + p.bottleQuantity, 0) ||
        0;
      const totalVolume =
        quotation.deliveryContent?.reduce((acc, c) => acc + c.numberOfBottles * c.volume, 0) ||
        quotation.quotationProducts?.reduce((acc, p) => acc + p.bottleQuantity * p.volume, 0) ||
        0;

      return (
        <div className="flex flex-col gap-0 px-2 text-black">
          <span className="text-[1rem] font-medium">
            {t("btlsVolCell.units", { count: nbBottles })}
          </span>
          <span className="text-xs">{totalVolume} L</span>
        </div>
      );
    },
  });

export const packagingColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "packaging",
    header: translatedHeader((t) => t("columns.packaging")),
    size: 80,
    cell: (info) => (
      <div className="flex justify-center">
        <PackagingTypeBadge expedition={info.row.original} />
      </div>
    ),
  });

export const incotermColumn = (options?: { header?: string }): ColumnDef<Row> =>
  columnHelper.display({
    id: "incoterm",
    header: options?.header ?? translatedHeader((t) => t("columns.incoterm")),
    size: 80,
    cell: (info) => (
      <div className="flex justify-center">
        <Incoterm dapOption={info.row.original.expeditionOptions.dapOption} />
      </div>
    ),
  });

const DateTimeCell = ({ date }: { date: string }) => {
  const d = new Date(date);
  return (
    <div className="flex flex-col gap-0">
      <span className="text-[1rem] text-black">{d.toLocaleDateString("fr-FR")}</span>
      <span className="text-xs text-black">
        {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
      </span>
    </div>
  );
};

const simpleDateColumn = ({
  id,
  accessorKey,
  header,
  size = 130,
  cellClassName,
  getDate,
}: {
  id: string;
  accessorKey?: keyof Row & string;
  header: ColumnDef<Row>["header"];
  size?: number;
  cellClassName?: string;
  getDate: (row: Row) => string | null | undefined;
}): ColumnDef<Row> => {
  const base = {
    id,
    header,
    sortDescFirst: true,
    size,
    ...(cellClassName ? { meta: { cellClassName } } : {}),
    cell: (info: { row: { original: Row } }) => {
      const date = getDate(info.row.original);
      if (!date) return null;
      return <DateTimeCell date={date} />;
    },
  };

  return accessorKey ? { ...base, accessorKey } : columnHelper.display(base);
};

export const validatedAtColumn = (): ColumnDef<Row> =>
  simpleDateColumn({
    id: "validatedAt",
    accessorKey: "validatedAt",
    header: translatedHeader((t) => t("columns.validatedAt")),
    size: 110,
    getDate: (row) => row.validatedAt,
  });

export const blockedAtColumn = (options?: { header?: string }): ColumnDef<Row> =>
  simpleDateColumn({
    id: "blockedAt",
    accessorKey: "blockedAt",
    header: options?.header ?? translatedHeader((t) => t("columns.blockedAt")),
    cellClassName: "bg-gray-100/60",
    getDate: (row) => row.blockedAt,
  });

export const unblockedAtColumn = (options?: { header?: string }): ColumnDef<Row> =>
  simpleDateColumn({
    id: "unblockedAt",
    accessorKey: "unblockedAt",
    header: options?.header ?? translatedHeader((t) => t("columns.unblockedAt")),
    cellClassName: "bg-gray-100/60",
    getDate: (row) => row.unblockedAt,
  });

export const blockedReasonColumn = (options?: { header?: string }): ColumnDef<Row> =>
  columnHelper.display({
    id: "blockedReason",
    header: options?.header ?? translatedHeader((t) => t("columns.blockedReason")),
    size: 180,
    enableSorting: false,
    cell: (info) => (
      <div className="px-1" onClick={(e) => e.stopPropagation()}>
        <BlockedReasonSelect expedition={info.row.original} />
      </div>
    ),
  });

export const deletedAtColumn = (): ColumnDef<Row> =>
  simpleDateColumn({
    id: "deletedAt",
    accessorKey: "deletedAt",
    header: translatedHeader((t) => t("columns.deletedAt")),
    getDate: (row) => row.deletedAt,
  });

export const deliveryDateColumn = (): ColumnDef<Row> =>
  simpleDateColumn({
    id: "deliveryDate",
    accessorKey: "deliveryDate",
    header: translatedHeader((t) => t("columns.deliveryDate")),
    getDate: (row) => row.deliveryDate,
  });

export const firstScanAtColumn = (options?: { header?: string }): ColumnDef<Row> =>
  simpleDateColumn({
    id: "firstScanAt",
    header: options?.header ?? translatedHeader((t) => t("columns.firstScanAt")),
    getDate: (row) => row.tracking?.firstScanAt,
  });

export const deliveryStatusBadgeColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "deliveryStatusBadge",
    header: translatedHeader((t) => t("columns.deliveryStatusBadge")),
    size: 80,
    enableSorting: false,
    cell: (info) => (
      <div className="flex justify-center pr-2">
        <DeliveryStatusBadge type={info.row.original.deliveryStatus as DeliveryStatusEnum} />
      </div>
    ),
  });

export const createdAtColumn = (): ColumnDef<Row> =>
  simpleDateColumn({
    id: "createdAt",
    accessorKey: "createdAt",
    header: translatedHeader((t) => t("columns.createdAt")),
    getDate: (row) => row.createdAt,
  });

export const deletedByColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "deletedBy",
    header: translatedHeader((t) => t("columns.deletedBy")),
    size: 130,
    enableSorting: false,
    cell: (info) => {
      const deletedBy = info.row.original.deletedBy;
      if (!deletedBy) return <span className="px-1 text-sm text-gray-400">-</span>;
      const fullName = `${deletedBy.firstName} ${deletedBy.lastName}`;
      return <span className="px-1 text-sm text-gray-900">{fullName}</span>;
    },
  });

export const assignedToColumn = (
  setActionData: (actionData: ActionDataBo) => void,
  user: User | null,
): ColumnDef<Row> => {
  const canAssign = user?.role === "ADMIN" || user?.role === "SUPERADMIN";

  return columnHelper.display({
    id: "assignedTo",
    header: translatedHeader((t) => t("columns.assignedTo")),
    size: 50,
    cell: function AssignedToCell(info) {
      const { t } = useTranslation("boExpeditions");
      const row = info.row.original;
      const assignedTo = (row as any).assignedTo as
        | { id: string; firstName: string; lastName: string }
        | null
        | undefined;
      const initials = assignedTo ? getInitials(assignedTo) : "—";

      if (!canAssign) {
        return (
          <span className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 p-2 text-sm font-medium">
            {initials}
          </span>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="mx-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 p-2 text-sm font-medium hover:opacity-70"
              onClick={(e) => {
                e.stopPropagation();
                setActionData({ expedition: row, action: "ASSIGN" });
              }}
            >
              {initials}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {assignedTo
              ? t("assignedToCell.assignedTo", {
                  name: `${assignedTo.firstName} ${assignedTo.lastName}`,
                })
              : t("assignedToCell.clickToAssign")}
          </TooltipContent>
        </Tooltip>
      );
    },
  });
};

type PendingDispatchStatus = "SENT_TO_CLIENT" | "TO_BE_SENT_TO_CLIENT";

const getDispatchConfirmCopy = (
  t: ExpeditionsT,
): Record<PendingDispatchStatus, { title: string; text: string }> => ({
  SENT_TO_CLIENT: {
    title: t("dispatchStatusCell.markSentConfirmTitle"),
    text: t("dispatchStatusCell.markSentConfirmDescription"),
  },
  TO_BE_SENT_TO_CLIENT: {
    title: t("dispatchStatusCell.cancelSentConfirmTitle"),
    text: t("dispatchStatusCell.cancelSentConfirmDescription"),
  },
});

function DispatchStatusCell({ row }: { row: Row }) {
  const { t } = useTranslation("boExpeditions");
  const { mutate: updateExpedition } = useUpdateExpeditionBo();
  const [pendingStatus, setPendingStatus] = useState<PendingDispatchStatus | null>(null);
  const status = row.dispatchStatus;

  const handleConfirm = () => {
    if (!pendingStatus) return;
    updateExpedition({ id: row.id, payload: { dispatchStatus: pendingStatus } });
    setPendingStatus(null);
  };

  return (
    <>
      {pendingStatus && (
        <ConfirmationModal
          open={true}
          onClose={() => setPendingStatus(null)}
          title={getDispatchConfirmCopy(t)[pendingStatus].title}
          text={getDispatchConfirmCopy(t)[pendingStatus].text}
          confirm={handleConfirm}
        />
      )}

      {status === "TO_BE_PRINTED" && (
        <div className="flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleX className="h-5 w-5 text-red-600" />
            </TooltipTrigger>
            <TooltipContent>{t("dispatchStatusCell.toBePrinted")}</TooltipContent>
          </Tooltip>
        </div>
      )}

      {status === "TO_BE_SENT_TO_CLIENT" && (
        <div className="flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="cursor-pointer rounded p-0.5 hover:bg-orange-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingStatus("SENT_TO_CLIENT");
                }}
              >
                <Send className="h-5 w-5 text-orange-500" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t("dispatchStatusCell.toBeSentTooltip")}</TooltipContent>
          </Tooltip>
        </div>
      )}

      {status === "SENT_TO_CLIENT" && (
        <div className="flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="cursor-pointer rounded p-0.5 hover:bg-orange-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingStatus("TO_BE_SENT_TO_CLIENT");
                }}
              >
                <MailCheck className="h-5 w-5 text-green-500" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t("dispatchStatusCell.sentTooltip")}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </>
  );
}

export const dispatchStatusColumn = (): ColumnDef<Row> =>
  columnHelper.display({
    id: "dispatchStatus",
    header: translatedHeader((t) => t("columns.dispatchStatus")),
    size: 50,
    enableSorting: false,
    cell: (info) => (
      <div onClick={(e) => e.stopPropagation()}>
        <DispatchStatusCell row={info.row.original} />
      </div>
    ),
  });

const noopSetActionData = (_: ActionDataBo) => {};

type UseExpeditionHistoryColumnsOptions = {
  setActionData?: (actionData: ActionDataBo) => void;
  withDeletedColumns?: boolean;
  cotationOnly?: boolean;
};

export const useExpeditionHistoryColumns = (
  customs: CustomReducedDto[],
  {
    cotationOnly = false,
    setActionData = noopSetActionData,
    withDeletedColumns = false,
  }: UseExpeditionHistoryColumnsOptions = {},
): ColumnDef<Row>[] => {
  const { t } = useTranslation("boExpeditions");
  return useMemo(
    () => [
      identifierColumn(setActionData, { cotationOnly }),
      envoiColumn(),
      ...(withDeletedColumns ? [deletedAtColumn(), deletedByColumn()] : []),
      validatedAtColumn(),
      pickupDateColumn({ header: t("historyColumns.pickupDateHeader"), showDeliveryBadge: true }),
      finalizedAtColumn(),
      clientColumn(),
      clientDestColumn(),
      countryColumn(customs),
      btlsVolColumn(),
      incotermColumn({ header: t("historyColumns.incotermHeader") }),
      trackingColumn(),
      deliveryDateColumn(),
    ],
    [customs, cotationOnly, setActionData, withDeletedColumns, t],
  );
};
